import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import '../auth/Signup.css';

// ─── FORGOT PASSWORD ─────────────────────────────────────────────────────────
export const ForgotPassword = ({ onNavigate }) => {
  const [email,     setEmail]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }
    setLoading(true);
    setError('');

    const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setLoading(false);

    if (authError) {
      setError('Something went wrong. Please try again or contact admin@umpire-iq.com');
      return;
    }

    setSent(true);
  };

  return (
    <div className="signup-page">
      <nav className="signup-nav">
        <div className="signup-nav-logo" onClick={() => onNavigate('home')}>
          Umpire<span>IQ</span>
        </div>
        <span className="signup-nav-login">
          Remember your password?
          <a href="#" onClick={e => { e.preventDefault(); onNavigate('login'); }}> Sign in</a>
        </span>
      </nav>

      <div className="signup-body">
        <div className="signup-card">

          {sent ? (
            <div className="signup-success">
              <div className="signup-success-icon">✉️</div>
              <h2>Check your email</h2>
              <p>We sent a password reset link to <strong>{email}</strong></p>
              <p>Click the link in the email to set a new password. The link expires in 1 hour.</p>
              <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--muted-light)' }}>
                Didn't get it? Check your spam folder or{' '}
                <button
                  onClick={() => setSent(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: '13px', padding: 0 }}
                >
                  try again
                </button>
              </p>
              <button
                className="signup-btn-primary"
                style={{ marginTop: '24px', width: '100%' }}
                onClick={() => onNavigate('login')}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <div className="signup-step-eyebrow">Account recovery</div>
              <h2>Forgot your password?</h2>
              <p className="signup-card-sub">
                Enter the email address associated with your account and we'll send you a reset link.
              </p>

              {error && <div className="signup-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="signup-field">
                  <label className="signup-label">Email address</label>
                  <input
                    className="signup-input"
                    type="email"
                    placeholder="your@firm.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>

                <div className="signup-btn-row">
                  <button
                    type="button"
                    className="signup-btn-back"
                    onClick={() => onNavigate('login')}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="signup-btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send reset link'}
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── RESET PASSWORD ───────────────────────────────────────────────────────────
// This page handles the link from the email
// Supabase puts the token in the URL hash on redirect
export const ResetPassword = ({ onNavigate }) => {
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading,         setLoading]         = useState(false);
  const [done,            setDone]            = useState(false);
  const [error,           setError]           = useState('');
  const [validSession,    setValidSession]    = useState(false);

  // Check for valid recovery session on load
  useEffect(() => {
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setValidSession(true);
      }
    });
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (!password)                        { setError('Please enter a new password'); return; }
    if (password.length < 8)              { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword)     { setError('Passwords do not match'); return; }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });

    setLoading(false);

    if (updateError) {
      setError(updateError.message || 'Failed to update password. Please try again.');
      return;
    }

    setDone(true);

    // Sign out and redirect to login after 3 seconds
    setTimeout(async () => {
      await supabase.auth.signOut();
      onNavigate('login');
    }, 3000);
  };

  // Invalid or expired link
  if (!validSession) {
    return (
      <div className="signup-page">
        <nav className="signup-nav">
          <div className="signup-nav-logo" onClick={() => onNavigate('home')}>
            Umpire<span>IQ</span>
          </div>
        </nav>
        <div className="signup-body">
          <div className="signup-card">
            <div className="signup-success">
              <div className="signup-success-icon">⚠️</div>
              <h2>Invalid or expired link</h2>
              <p>This password reset link is invalid or has expired.</p>
              <p>Reset links are valid for 1 hour. Please request a new one.</p>
              <button
                className="signup-btn-primary"
                style={{ marginTop: '24px', width: '100%' }}
                onClick={() => onNavigate('forgot')}
              >
                Request new reset link
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <nav className="signup-nav">
        <div className="signup-nav-logo" onClick={() => onNavigate('home')}>
          Umpire<span>IQ</span>
        </div>
      </nav>

      <div className="signup-body">
        <div className="signup-card">

          {done ? (
            <div className="signup-success">
              <div className="signup-success-icon">✅</div>
              <h2>Password updated</h2>
              <p>Your password has been successfully updated.</p>
              <p style={{ fontSize: '13px', color: 'var(--muted-light)' }}>
                Redirecting you to sign in...
              </p>
            </div>
          ) : (
            <>
              <div className="signup-step-eyebrow">Account recovery</div>
              <h2>Set new password</h2>
              <p className="signup-card-sub">
                Choose a strong password for your UmpireIQ account.
              </p>

              {error && <div className="signup-error">{error}</div>}

              <form onSubmit={handleReset}>
                <div className="signup-field">
                  <label className="signup-label">New password</label>
                  <input
                    className="signup-input"
                    type="password"
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="signup-field">
                  <label className="signup-label">Confirm new password</label>
                  <input
                    className="signup-input"
                    type="password"
                    placeholder="Re-enter your new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="signup-btn-row">
                  <button
                    type="submit"
                    className="signup-btn-primary"
                    style={{ width: '100%' }}
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update password'}
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
